import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Mic,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  Target,
  Award,
  Loader2,
  Volume2,
  MessageSquare,
  Sparkles,
  AlertCircle,
  ChevronRight,
  Calendar,
  BarChart3,
  Lightbulb,
  Play,
  RefreshCw,
  Info
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

const SpeechProfile = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [trends, setTrends] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, sessionsRes, trendsRes] = await Promise.all([
        api.get('/speech/profile'),
        api.get('/speech/sessions?limit=20'),
        api.get('/speech/trends?days=30').catch(() => ({ data: null }))
      ]);

      setHasProfile(profileRes.data.has_profile);
      setProfile(profileRes.data.profile);
      setSessions(sessionsRes.data.sessions || []);
      setTrends(trendsRes.data);
    } catch (error) {
      console.error('Failed to fetch speech data:', error);
      toast.error('Failed to load speech profile');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="h-4 w-4 text-emerald-500" />;
      case 'declining':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-slate-400" />;
    }
  };

  const getTrendBadge = (trend) => {
    switch (trend) {
      case 'improving':
        return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Improving</Badge>;
      case 'declining':
        return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Needs Focus</Badge>;
      default:
        return <Badge variant="secondary">Stable</Badge>;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!hasProfile) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto mt-12">
          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border-violet-200 dark:border-violet-800">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-violet-500/10 flex items-center justify-center mb-6">
                <Mic className="h-10 w-10 text-violet-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Build Your Speech Profile</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Complete voice sessions in simulations, role plays, or assessments to start 
                tracking your communication patterns and see your improvements over time.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button size="lg" className="gap-2" onClick={() => window.location.href = '/simulation'}>
                  <Play className="h-5 w-5" />
                  Start Simulation
                </Button>
                <Button size="lg" variant="outline" className="gap-2" onClick={() => window.location.href = '/roleplay'}>
                  <MessageSquare className="h-5 w-5" />
                  Try Role Play
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Prepare chart data
  const chartData = trends?.dates?.map((date, idx) => ({
    date: date,
    clarity: trends.clarity_scores?.[idx] || 0,
    fillerRate: trends.filler_rates?.[idx] || 0,
    pace: trends.speaking_paces?.[idx] || 0,
    vocabulary: trends.vocabulary_diversity?.[idx] || 0
  })) || [];

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="speech-profile-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600">
                <Mic className="h-6 w-6 text-white" />
              </div>
              My Voice Profile
            </h1>
            <p className="text-muted-foreground mt-1">
              Track your communication patterns and improvements
            </p>
          </div>
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Main Score Card */}
        <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-0">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Clarity Score */}
              <div className="text-center md:border-r md:border-white/10">
                <div className="relative w-32 h-32 mx-auto">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="none" className="text-white/10" />
                    <circle
                      cx="64" cy="64" r="56"
                      stroke="url(#clarityGradient)"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${(profile.avg_clarity_score / 100) * 352} 352`}
                    />
                    <defs>
                      <linearGradient id="clarityGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold">{Math.round(profile.avg_clarity_score)}</span>
                    <span className="text-xs text-slate-400">Clarity</span>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 mt-3">
                  {getTrendIcon(profile.clarity_trend)}
                  <span className="text-sm text-slate-300">
                    {profile.clarity_trend === 'improving' ? 'Getting better!' : profile.clarity_trend === 'declining' ? 'Needs focus' : 'Stable'}
                  </span>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-4 md:col-span-2">
                <div className="p-4 rounded-xl bg-white/5">
                  <p className="text-3xl font-bold">{profile.avg_filler_word_rate.toFixed(1)}%</p>
                  <p className="text-sm text-slate-400">Filler Word Rate</p>
                  <div className="flex items-center gap-1 mt-1">
                    {getTrendIcon(profile.filler_word_trend)}
                    <span className="text-xs text-slate-400">{profile.filler_word_trend}</span>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-white/5">
                  <p className="text-3xl font-bold">{Math.round(profile.avg_speaking_pace)}</p>
                  <p className="text-sm text-slate-400">Words/Minute</p>
                  <p className="text-xs text-slate-500 mt-1">Ideal: 120-160 WPM</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5">
                  <p className="text-3xl font-bold">{(profile.avg_vocabulary_diversity * 100).toFixed(0)}%</p>
                  <p className="text-sm text-slate-400">Vocabulary Diversity</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5">
                  <p className="text-3xl font-bold">{profile.total_sessions}</p>
                  <p className="text-sm text-slate-400">Sessions Analyzed</p>
                  <p className="text-xs text-slate-500 mt-1">{profile.sessions_this_week} this week</p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-amber-400" />
                    <span className="text-sm">Best Score: {Math.round(profile.best_clarity_score)}</span>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-blue-400" />
                    <span className="text-sm">{profile.total_speaking_time_minutes.toFixed(0)} min total</span>
                  </div>
                </div>
                {profile.recent_improvement_pct > 0 && (
                  <div className="p-3 rounded-lg bg-emerald-500/20 border border-emerald-500/30">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-emerald-400" />
                      <span className="text-sm text-emerald-300">+{profile.recent_improvement_pct}% improved</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for different views */}
        <Tabs defaultValue="insights" className="space-y-4">
          <TabsList>
            <TabsTrigger value="insights" className="gap-2">
              <Lightbulb className="h-4 w-4" />
              Insights
            </TabsTrigger>
            <TabsTrigger value="trends" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Trends
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <Calendar className="h-4 w-4" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Personalized Tips */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-amber-500" />
                    Personalized Tips
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {profile.personalized_tips?.length > 0 ? (
                    profile.personalized_tips.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-primary">{idx + 1}</span>
                        </div>
                        <p className="text-sm">{tip}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">Keep practicing to get personalized tips!</p>
                  )}
                </CardContent>
              </Card>

              {/* Most Used Fillers */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-500" />
                    Top Filler Words
                  </CardTitle>
                  <CardDescription>Words to be aware of</CardDescription>
                </CardHeader>
                <CardContent>
                  {Object.keys(profile.most_used_fillers || {}).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(profile.most_used_fillers).slice(0, 5).map(([word, count]) => (
                        <div key={word} className="flex items-center gap-3">
                          <Badge variant="outline" className="font-mono">{word}</Badge>
                          <div className="flex-1">
                            <Progress value={Math.min((count / 20) * 100, 100)} className="h-2" />
                          </div>
                          <span className="text-sm text-muted-foreground w-12 text-right">{count}x</span>
                        </div>
                      ))}
                      <p className="text-xs text-muted-foreground mt-2">
                        Tip: Try pausing instead of using filler words
                      </p>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">Great job! Minimal filler words detected.</p>
                  )}
                </CardContent>
              </Card>

              {/* Strengths */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-emerald-500" />
                    Your Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {profile.strongest_areas?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.strongest_areas.map((strength, idx) => (
                        <Badge key={idx} className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          {strength}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">Complete more sessions to identify strengths</p>
                  )}
                </CardContent>
              </Card>

              {/* Areas to Improve */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-500" />
                    Focus Areas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {profile.improvement_areas?.length > 0 ? (
                    <div className="space-y-2">
                      {profile.improvement_areas.map((area, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                          <ChevronRight className="h-4 w-4 text-blue-500" />
                          <span className="text-sm">{area}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">No specific areas identified yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-4">
            {chartData.length > 2 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Clarity Score Trend */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Clarity Score Over Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <defs>
                            <linearGradient id="clarityFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Area type="monotone" dataKey="clarity" stroke="#10b981" fill="url(#clarityFill)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Filler Word Rate Trend */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Filler Word Rate</CardTitle>
                    <CardDescription>Lower is better</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="fillerRate" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Speaking Pace */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Speaking Pace (WPM)</CardTitle>
                    <CardDescription>Ideal range: 120-160</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis domain={[80, 200]} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="pace" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Vocabulary Diversity */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Vocabulary Diversity</CardTitle>
                    <CardDescription>Higher is better</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <defs>
                            <linearGradient id="vocabFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Area type="monotone" dataKey="vocabulary" stroke="#3b82f6" fill="url(#vocabFill)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Info className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Not Enough Data Yet</h3>
                  <p className="text-muted-foreground">
                    Complete at least 3 voice sessions to see your trends over time.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Session History</CardTitle>
                <CardDescription>Your recent voice sessions</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {sessions.length > 0 ? (
                    <div className="space-y-3">
                      {sessions.map((session) => (
                        <div
                          key={session.id}
                          className="p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => setSelectedSession(session)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="capitalize">
                                {session.session_type}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {session.analyzed_at ? formatDistanceToNow(new Date(session.analyzed_at), { addSuffix: true }) : 'N/A'}
                              </span>
                            </div>
                            <div className={`text-lg font-bold ${getScoreColor(session.metrics?.clarity_score || 0)}`}>
                              {Math.round(session.metrics?.clarity_score || 0)}
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Filler Rate:</span>
                              <span className="ml-1 font-medium">{(session.metrics?.filler_word_rate || 0).toFixed(1)}%</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Pace:</span>
                              <span className="ml-1 font-medium">{Math.round(session.metrics?.speaking_pace_wpm || 0)} WPM</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Words:</span>
                              <span className="ml-1 font-medium">{session.metrics?.word_count || 0}</span>
                            </div>
                          </div>
                          {session.ai_feedback && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{session.ai_feedback}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Mic className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No sessions yet. Complete a voice session to see your history.</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default SpeechProfile;
